import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, X } from 'lucide-react'
import { useAuth } from '@/lib/AuthContext'
import { onPlanLimit, type PlanLimitAnnouncement } from '@/lib/planLimitNotice'
import { PlanUpgradeLink } from './PlanLimitNotice'

interface Toast { id: number; text: string; upgrade: boolean }

/** Long enough to read a sentence and reach the link; the person can close it sooner. */
const TOAST_MS = 12000

/**
 * F-34: what a plan limit did to an upload ("the video will play the original
 * file", "storage is full"), wherever the upload happened. Mounted once in the shell.
 */
export function PlanLimitToaster() {
  const { t } = useTranslation('entitlements')
  const { hasRole } = useAuth()
  const [toasts, setToasts] = React.useState<Toast[]>([])
  const canChangePlan = hasRole(['owner', 'admin'])

  React.useEffect(() => onPlanLimit((notice: PlanLimitAnnouncement) => {
    let text = ''
    let upgrade = true
    if (notice.kind === 'transcode') {
      if (notice.code === 'transcodeQuotaReached' || notice.code === 'transcodeNotOnPlan' || notice.code === 'transcodeTrialSpent') text = t(`toast.${notice.code}`)
      else { text = notice.message || t('toast.streamingUnavailable'); upgrade = false }
    } else if (notice.kind === 'storage') {
      /* The upload form already shows the API's refusal; the toast is only the way out, for who can take it. */
      if (!canChangePlan) return
      text = t('toast.storageFull')
    }
    if (!text) return
    const id = Date.now() + Math.random()
    setToasts((list) => [...list, { id, text, upgrade }])
    window.setTimeout(() => setToasts((list) => list.filter((x) => x.id !== id)), TOAST_MS)
  }), [t, canChangePlan])

  if (!toasts.length) return null
  return (
    <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[60] flex flex-col items-center gap-2 sm:inset-x-auto sm:right-4 sm:items-end" aria-live="polite">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          data-testid="plan-limit-toast"
          className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/95 p-4 text-amber-900 shadow-lg backdrop-blur-sm"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <p className="min-w-0 flex-1 text-sm">
            {toast.text}
            {toast.upgrade && <> <PlanUpgradeLink /></>}
          </p>
          <button
            type="button"
            aria-label={t('toast.dismiss')}
            className="flex-shrink-0 text-amber-700 hover:text-amber-900"
            onClick={() => setToasts((list) => list.filter((x) => x.id !== toast.id))}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  )
}
